import { Checkbox, InlineNotification } from "@carbon/react";
import i18next from "../../i18n";
import { OffersToAccept } from "../../types";
import { currencyFormatter } from "../../utils";
import "./awardJoint.scss";

type Props = {
  isBestSelected: boolean;
  onToggleBest: () => void;
  bestTotal: number;
  bestCoin: string | null;
  selectionTotal: number;
  offersToAccept: OffersToAccept[];
  selectedCurrencies: string[];
  hasMultipleCurrencies: boolean;
  /** A closed award: no "select best" shortcut, and the selection reads as what was awarded. */
  readOnly?: boolean;
};

/**
 * The two summary cards above the joint award cards: "best possible total" (with the
 * `SELECT_BEST_OFFERS` shortcut) and the running "your selection" total (with a per-currency
 * breakdown and a multi-currency notice when suppliers span currencies). Carbon tokens, no
 * inline blue.
 */
const AwardJointSummary = ({
  isBestSelected,
  onToggleBest,
  bestTotal,
  bestCoin,
  selectionTotal,
  offersToAccept,
  selectedCurrencies,
  hasMultipleCurrencies,
  readOnly = false,
}: Props) => (
  <div className="award-joint-summary">
    <div className="award-joint-summary__card">
      {!readOnly && (
        <label className="award-joint-summary__toggle">
          <Checkbox
            id="award-joint-select-best"
            labelText={i18next.t("award.selectBest")}
            checked={isBestSelected}
            onChange={onToggleBest}
          />
        </label>
      )}
      <div className="award-joint-summary__label">
        {i18next.t("award.bestPossibleTotal")}
      </div>
      <div className="award-joint-summary__value award-joint-summary__value--best">
        {currencyFormatter(bestTotal, bestCoin ?? undefined)}
      </div>
    </div>

    <div className="award-joint-summary__card">
      <div className="award-joint-summary__label">
        {i18next.t(readOnly ? "award.awarded" : "award.yourSelection")}
      </div>
      {hasMultipleCurrencies ? (
        <>
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title={i18next.t("award.multipleCurrencies")}
          />
          <div>
            {selectedCurrencies.map((currency) => {
              const currencyTotal = offersToAccept
                .filter((offer) => offer.offer?.currency === currency)
                .reduce((total, offer) => {
                  const volume = Number(offer.volume) || 0;
                  const price = Number(offer.price) || 0;
                  return total + volume * price;
                }, 0);
              return (
                <div
                  key={currency}
                  className="award-joint-summary__value"
                  style={{ fontSize: "0.875rem" }}
                >
                  {currencyFormatter(currencyTotal, currency)} ({currency})
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="award-joint-summary__value">
          {currencyFormatter(
            selectionTotal,
            selectedCurrencies[0] || bestCoin || undefined
          ) ?? 0}
        </div>
      )}
    </div>
  </div>
);

export default AwardJointSummary;
